/**
 * Contracts — §8.1, TD-04, FR-01…FR-05.
 */
import { z } from 'zod';
import { AgreementFamilyIdSchema, ContractKeySchema, HttpsUrlSchema, IsoDateSchema, IsoTimestampUtcSchema, OpaqueIdSchema, PartyKeySchema, UpnSchema, VersionNoSchema, } from '../primitives.js';
import { AmendmentSignalNameSchema, ContractTypeAutoConfirmationKindSchema, ContractTypeConfirmationSchema, ContractTypeDecidedBySchema, ContractTypeDecisionKindSchema, ContractTypeNameEvidenceSchema, ContractTypeProposalSchema, ContractTypeProvisionalReasonSchema, ContractTypeSchema, isSettledTypeConfirmation, DataTrustSchema, IntakePathSchema, LifecycleStatusSchema, ObligationStatusSchema, ProcessingStatusSchema, SecurityClassificationSchema, VerificationStatusSchema, } from '../vocabulary.js';
import { AiProcessingProvenanceSchema, ConversionCaveatSchema, ConversionProvenanceSchema, provenanced, } from '../provenance.js';
import { ContractPartySchema } from './party.js';
import { TAGS_PER_CONTRACT_MAX, TagSchema } from '../endpoints/contract-tags.js';
import { CloudeqEntityKeySchema, CloudeqEntitySourceSchema } from './cloudeq-entity.js';
/* -------------------------------------------------------------------------- */
/* Lineage — §8.1, three distinct relationships                                */
/* -------------------------------------------------------------------------- */
/**
 * The §8.1 lineage block — all three relationships as separate fields, because
 * they mean different things (TD-04):
 *
 * | Field | Meaning | Example |
 * |---|---|---|
 * | `parent_contract_key` | **structural** — this sits under that | SOW → its MSA |
 * | `amends_contract_key` | **modifies** — the parent keeps its status; governing = parent + amendments in sequence | `SGW-MSA-0003-A1` → `SGW-MSA-0003` |
 * | `supersedes_contract_key` | **replaces** — the target flips to `Superseded` and read-only | `SGW-MSA-0009` → `SGW-MSA-0003` |
 *
 * DR-02: lineage is explicit, **never parsed from the contract key**.
 */
export const ContractLineageSchema = z.object({
    agreement_family_id: AgreementFamilyIdSchema,
    version_no: VersionNoSchema,
    is_current_version: z.boolean(),
    parent_contract_key: ContractKeySchema.nullable(),
    /**
     * §8.1 Rule 4 — an amendment **inherits parties, owner, security and category
     * from its parent**, which means access to an amendment is decided by the
     * parent's classification and not its own.
     *
     * Server-side scope resolution therefore walks *up* this edge before it builds
     * the query predicate. Miss that and amendments become visible to people who
     * cannot see the agreement they modify. It is an output here and never an input:
     * a caller cannot nominate a parent to inherit access from.
     */
    amends_contract_key: ContractKeySchema.nullable(),
    supersedes_contract_key: ContractKeySchema.nullable(),
    /** Keys that amend this contract, in sequence. Governing text = this + these. */
    amended_by_contract_keys: z.array(ContractKeySchema),
    /** Set when this contract has itself been replaced (§8.1 Rule 2). */
    superseded_by_contract_key: ContractKeySchema.nullable(),
});
/* -------------------------------------------------------------------------- */
/* Dates and terms — every value provenanced                                   */
/* -------------------------------------------------------------------------- */
/**
 * FR-01 and FR-04 dates. `renewal_notice_date` is the O-1 payload — the field the
 * whole notification service exists to watch.
 */
export const ContractDatesSchema = z.object({
    effective_date: provenanced(IsoDateSchema.nullable()),
    execution_date: provenanced(IsoDateSchema.nullable()),
    expiry_date: provenanced(IsoDateSchema.nullable()),
    /** Drives NFR-08 retention: disposal is measured from termination, not upload. */
    termination_date: provenanced(IsoDateSchema.nullable()),
    renewal_notice_date: provenanced(IsoDateSchema.nullable()),
    /** Where the notice period is expressed as a day count rather than a date. */
    renewal_notice_days: provenanced(z.number().int().min(0).nullable()),
    auto_renew: provenanced(z.boolean().nullable()),
});
/**
 * FR-02 structured terms.
 *
 * Every term is provenanced individually rather than the block as a whole,
 * because FR-07 verification is per field: `field_verification` is keyed on
 * `(contract_key, field_name)`, and a reviewer confirms a liability cap without
 * thereby confirming a payment term.
 *
 * Values are strings, not parsed money or durations. The extraction schema
 * (`wp-03` §3) keeps them verbatim: *"report only what the document states"*, and
 * a contract value of "the greater of £250,000 or fees paid in the preceding
 * twelve months" has no numeric representation.
 */
export const ContractKeyTermsSchema = z.object({
    contract_value: provenanced(z.string().max(400).nullable()),
    currency: provenanced(z.string().max(8).nullable()),
    payment_terms: provenanced(z.string().max(400).nullable()),
    liability_cap: provenanced(z.string().max(400).nullable()),
    indemnity_scope: provenanced(z.string().max(400).nullable()),
    insurance_requirements: provenanced(z.string().max(400).nullable()),
    governing_law: provenanced(z.string().max(400).nullable()),
    jurisdiction: provenanced(z.string().max(400).nullable()),
    exclusivity: provenanced(z.string().max(400).nullable()),
});
/* -------------------------------------------------------------------------- */
/* Obligations — FR-03, §3.6                                                   */
/* -------------------------------------------------------------------------- */
/**
 * Owner resolution — §3.6. Extraction returns a role *string* ("the Supplier's
 * project lead"); a notification needs a mailbox.
 *
 * Discriminated so that `resolved` cannot be represented without the thing it
 * resolved to. `unresolved` is a visible state, not a silent default: unresolved
 * obligations appear on the admin dashboard, and notifications route to the
 * contract owner so nothing is dropped.
 */
export const ObligationOwnerSchema = z.discriminatedUnion('owner_status', [
    z.object({
        owner_status: z.literal('unresolved'),
        /** Verbatim, as extracted. */
        owner_role_text: z.string().min(1).max(200).nullable(),
    }),
    z.object({
        owner_status: z.literal('resolved'),
        owner_role_text: z.string().min(1).max(200).nullable(),
        /** A UPN or an Entra group name. */
        owner_resolved: z.string().min(1).max(200),
        /** How it resolved — `role_mapping` hit, or the contract-owner fallback. */
        resolution_source: z.enum(['role_mapping', 'contract_owner_fallback']),
    }),
    z.object({
        owner_status: z.literal('not_applicable'),
        owner_role_text: z.string().min(1).max(200).nullable(),
    }),
]);
export const ObligationSchema = z.object({
    obligation_id: OpaqueIdSchema,
    contract_key: ContractKeySchema,
    description: provenanced(z.string().min(1).max(1000)),
    due_date: provenanced(IsoDateSchema.nullable()),
    obligation_status: ObligationStatusSchema,
    owner: ObligationOwnerSchema,
    /** Clause reference as cited in the document, e.g. `§12.3`. */
    source_clause_ref: z.string().max(120).nullable(),
    created_utc: IsoTimestampUtcSchema,
});
/* -------------------------------------------------------------------------- */
/* Documents — IR-05                                                           */
/* -------------------------------------------------------------------------- */
/**
 * Pointers to the stored artefacts.
 *
 * Signed documents live in SharePoint and stay there — the facts about them live
 * in exactly one place and are *referenced* everywhere else, never copied. This
 * carries links, never bytes, and the front end never talks to SharePoint
 * directly (CLAUDE.md rule 11).
 */
export const ContractDocumentsSchema = z.object({
    sharepoint_item_url: HttpsUrlSchema.nullable(),
    completion_cert_url: HttpsUrlSchema.nullable(),
    docusign_envelope_id: z.string().max(64).nullable(),
    page_count: z.number().int().min(1).nullable(),
});
/* -------------------------------------------------------------------------- */
/* GDPR — CR-03 / TD-03                                                        */
/* -------------------------------------------------------------------------- */
/**
 * TD-03 mechanism 1 — detected at extraction, so the GDPR position on any
 * contract is a query rather than an audit.
 */
export const PersonalDataAssessmentSchema = z.object({
    contains_personal_data: provenanced(z.boolean()),
    /** e.g. signatory names, contact details, key-personnel schedules. */
    categories: z.array(z.string().min(1).max(120)),
});
/* -------------------------------------------------------------------------- */
/* Contract summary — the search-result / list shape                           */
/* -------------------------------------------------------------------------- */
/**
 * The lightweight contract shape: enough for a result row or a list, without the
 * extracted body.
 *
 * `lifecycle_status` and `processing_status` both appear, and both must be
 * rendered. TD-04: a contract can be legally `Active` and governing while its
 * renewal date is still awaiting verification. Showing one without the other tells
 * the user something false.
 */
export const ContractSummarySchema = z.object({
    contract_key: ContractKeySchema,
    contract_type: ContractTypeSchema,
    /**
     * CLM-110 (R-110-23), CLM-118 — whether `contract_type` is settled, and by
     * what. A list renders it through `ContractTypeTag`: `pending` as "Type
     * pending" (never as `OTHER`), `provisional` with its tag, `auto_confirmed` as
     * "file name and AI agree" — a type nobody checked never reads as an Admin's.
     */
    contract_type_confirmation: ContractTypeConfirmationSchema,
    /** Denormalised for display: the `is_primary` party's cached name. */
    primary_party_display_name: z.string().min(1).max(200),
    primary_party_key: PartyKeySchema,
    /** CLM-99 — projected from the primary party's own row, same join as `primary_party_display_name`. */
    primary_party_billing_client_id: z.string().min(1).max(64).nullable(),
    primary_party_is_billable: z.boolean(),
    lifecycle_status: LifecycleStatusSchema,
    processing_status: ProcessingStatusSchema,
    /**
     * CLM-108 — the trust axis (B-02). `Verified` means every field was settled by
     * a person; `AutoApproved` means some were settled by policy (D-2.1-4). The
     * only value a list may derive "Verified" from: `processing_status` reaches
     * `Verified` for an auto-approved contract too, and `has_unverified_fields`
     * is false for a contract with no field row at all.
     */
    data_trust: DataTrustSchema,
    effective_date: IsoDateSchema.nullable(),
    expiry_date: IsoDateSchema.nullable(),
    renewal_notice_date: IsoDateSchema.nullable(),
    contract_owner_upn: UpnSchema.nullable(),
    is_current_version: z.boolean(),
    amends_contract_key: ContractKeySchema.nullable(),
    /**
     * True when any field on this contract is still awaiting human verification
     * (FR-07). The list surface needs this to warn without fetching every field.
     */
    has_unverified_fields: z.boolean(),
    /** §4C — no LLM has seen or will see this contract's content. */
    processing_restricted: z.boolean(),
    modified_utc: IsoTimestampUtcSchema,
});
/* -------------------------------------------------------------------------- */
/* Contract detail — the retrieve-contract shape                               */
/* -------------------------------------------------------------------------- */
/**
 * CLM-109 (R-109-12, R-109-15) — one admin-defined field's value on this
 * contract, read from its `field_verification` row, which is the value's only
 * home: there is no write-back to `clm.contract` (Q-109-8). `value` is the
 * `confirmed_value` once a reviewer settled the row `Verified` or `Corrected`,
 * and the `extracted_value` otherwise — `null` where the document is silent (or
 * the model's value was over its bound, which the queue item says). Display
 * only: not searchable, never a notification or dashboard input.
 */
export const ContractAdditionalTermSchema = z
    .object({
    field_key: z.string().regex(/^[a-z][a-z0-9_]{0,47}$/),
    field_name: z.string().min(1).max(80),
    value: z.string().max(400).nullable(),
    verification_status: VerificationStatusSchema,
})
    .strict()
    .refine((term) => term.field_name === `additional_terms.${term.field_key}`, {
    message: 'field_name is additional_terms.<field_key>',
    path: ['field_name'],
});
/**
 * CLM-110 / FR-35 — the classifier's proposal for this contract's type, as
 * D-110-3 amends FR-35 (CLM-118): the pipeline's case 2 applies it as the
 * provisional working type only where there is no trusted file-name type, and
 * 1a settles a trusted file-name type it agrees with. It never changes the key.
 * The evidence is the `AIExtraction` row `audit_id` names (CR-08, first half):
 * the model and schema version are read from it, not restated on the contract.
 */
export const ContractTypeProposalDetailSchema = z
    .object({
    proposed_type: ContractTypeProposalSchema,
    /**
     * Whether the proposal matches the current type. `null` when the two are not
     * comparable: a current type of `AMENDMENT`, which the model never proposes
     * (R-110-2), or a Type pending row, whose `OTHER` is a placeholder and not a
     * type to agree with (CLM-118, R-118-16).
     */
    agrees: z.boolean().nullable(),
    proposed_utc: IsoTimestampUtcSchema,
    extraction_model: z.string().min(1).max(120),
    extraction_schema_version: z.string().min(1).max(20),
    /** The `AIExtraction` row — a decimal string, as every audit id on the wire. */
    audit_id: z.string().regex(/^\d{1,19}$/),
})
    .strict();
/**
 * CLM-110 (§3.5) — what an Admin's decision on a family **flags but never
 * changes**, as the record shows it:
 *
 *  - `children_under_non_msa` — contracts `child_of` this one, which is not an MSA;
 *  - `msa_under_parent` — this MSA itself sits `child_of` another contract;
 *  - `supersession_type_differs` — a supersession edge whose two ends differ in
 *    type. Each contract in a chain is decided on its own.
 */
export const CONTRACT_TYPE_LINEAGE_NOTE_KINDS = [
    'children_under_non_msa',
    'msa_under_parent',
    'supersession_type_differs',
];
export const ContractTypeLineageNoteKindSchema = z.enum(CONTRACT_TYPE_LINEAGE_NOTE_KINDS);
export const ContractTypeLineageNoteSchema = z
    .object({
    kind: ContractTypeLineageNoteKindSchema,
    /** The other contracts the note is about, sorted. */
    contract_keys: z.array(ContractKeySchema).min(1),
})
    .strict();
/**
 * CLM-110 (R-110-17, R-110-18) — the Admin decision that confirmed a type, read
 * from its `HumanVerification` row (`field_name 'classification.contract_type'`).
 * For an amendment it is its **agreement's** decision — one row per decided
 * family (rule 7) — so `contract_key` names the agreement.
 */
export const ContractTypeDecisionSummarySchema = z
    .object({
    /** The `HumanVerification` row — a decimal string, as every audit id on the wire. */
    audit_id: z.string().regex(/^\d{1,19}$/),
    /** The agreement the row is written on. */
    contract_key: ContractKeySchema,
    decided_by_upn: UpnSchema,
    decided_utc: IsoTimestampUtcSchema,
    kind: ContractTypeDecisionKindSchema,
    /**
     * Whether CLM-107 counts it under `classification.contract_type`: only an
     * agreement's first decision after its own latest AI reading (R-110-18).
     */
    counted_for_accuracy: z.boolean(),
})
    .strict();
/** Every audit id on the wire is a decimal string. */
const SeenAuditIdSchema = z.string().regex(/^\d{1,19}$/, 'expected an audit id');
const isSortedUnique = (values) => values.every((value, index) => index === 0 || values[index - 1] < value);
/**
 * What the Admin looked at (R-110-17) — the `seen` block `decide-contract-type`
 * takes. Computed by the API, from the same family read the decision locks, on
 * the Type proposals row and on the record's `contract_type_status`, and posted
 * back unchanged.
 *
 *  - `contract_type`, `confirmation`, `confirmed_audit_id` — the root as shown;
 *  - `proposal_audit_id` / `proposed_type` — the root's **latest** AI reading:
 *    both `null` when it was never read, and `proposed_type` alone `null` for an
 *    answer outside the vocabulary (`ai_no_type`, R-110-28);
 *  - `family_contract_keys` — every family row the dialog listed, the root
 *    included, sorted;
 *  - `amendment_proposal_audit_ids` — the latest reading of each amendment that
 *    has one, so an amendment read after the page loaded is `stale_view`.
 *
 * Declared here, beside {@link ContractTypeStatusSchema} that carries it, rather
 * than in `endpoints/decide-contract-type.ts`: that module imports this one, so
 * the reverse import would be a cycle.
 */
export const ContractTypeDecisionSeenSchema = z
    .object({
    contract_type: ContractTypeSchema,
    proposed_type: ContractTypeProposalSchema.nullable(),
    proposal_audit_id: SeenAuditIdSchema.nullable(),
    confirmation: ContractTypeConfirmationSchema,
    confirmed_audit_id: SeenAuditIdSchema.nullable(),
    family_contract_keys: z.array(ContractKeySchema).min(1).max(1000),
    amendment_proposal_audit_ids: z.array(SeenAuditIdSchema).max(1000),
})
    .strict()
    .refine((seen) => seen.proposed_type === null || seen.proposal_audit_id !== null, {
    message: 'a proposed type names the reading it came from',
    path: ['proposal_audit_id'],
})
    .refine((seen) => isSettledTypeConfirmation(seen.confirmation) === (seen.confirmed_audit_id !== null), {
    message: 'confirmed_audit_id is set exactly when the type was seen settled (confirmed or auto_confirmed)',
    path: ['confirmed_audit_id'],
})
    .refine((seen) => isSortedUnique(seen.family_contract_keys), {
    message: 'family_contract_keys is sorted, without repeats',
    path: ['family_contract_keys'],
});
/**
 * CLM-118 (R-118-8) — the act that settled a type with **no person**: the
 * `contract-type-rule` audit row an `auto_confirmed` type cites. Never a
 * decision: it names nobody, and `kind` is read from the row's `type_rule`,
 * never from a `decision_kind` (which the row cannot carry). For an amendment
 * it is its agreement's act, so `contract_key` names the agreement.
 */
export const ContractTypeAutoConfirmationSchema = z
    .object({
    /** The `contract-type-rule` row — a decimal string, as every audit id on the wire. */
    audit_id: z.string().regex(/^\d{1,19}$/),
    /** The agreement the row is written on. */
    contract_key: ContractKeySchema,
    settled_utc: IsoTimestampUtcSchema,
    /** `file_name_and_ai_agree` (1a) or `auto_approval_policy` (approved by policy, CLM-121). */
    kind: ContractTypeAutoConfirmationKindSchema,
    /** The `AIExtraction` reading that agreed with the file name, or the one the policy approved. */
    reading_audit_id: z.string().regex(/^\d{1,19}$/),
})
    .strict();
/**
 * CLM-118 (R-118-9) — why the AI's type was **not applied** to an agreement with
 * no trusted file-name type: the `leave:` outcomes of the type rule that are
 * guards (B16's "with a note"). A subset of `CONTRACT_TYPE_RULE_OUTCOMES`:
 *
 *  - `typed_key` — the key names a type (filed before D-110-1; FR-35, CLM-119);
 *  - `sow_number_recorded` — a SOW whose number was recorded at filing (D-71-0);
 *  - `relationship_clash` — the AI's type would break a recorded relationship (B16);
 *  - `superseded` — the family has a superseded contract (B18);
 *  - `not_replaceable` — where the type came from was never recorded (a legacy
 *    type, or a file-name type filed before CLM-118's evidence existed).
 */
export const CONTRACT_TYPE_RULE_HOLDS = [
    'typed_key',
    'sow_number_recorded',
    'relationship_clash',
    'superseded',
    'not_replaceable',
];
export const ContractTypeRuleHoldSchema = z.enum(CONTRACT_TYPE_RULE_HOLDS);
/**
 * R-110-25 — a typed key's middle segment, carried only when it differs from the
 * type. Any 3–12 letters, as `ck_contract_key_format` admits — deliberately not
 * `ContractTypeSchema`: a segment outside the type vocabulary would otherwise
 * fail the response schema and turn the record into a 500.
 */
export const LEGACY_KEY_TYPE_SEGMENT_PATTERN = /^[A-Z]{3,12}$/;
/**
 * CLM-110 (R-110-13, R-110-23, R-110-25) — the type's settlement on the record.
 * Display only; the decision endpoint re-derives every rule from the locked rows.
 *
 *  - `confirmation` — the column; `provisional_reason` is its computed cause,
 *    set exactly while `provisional` or `pending` (CLM-118).
 *  - `follows_agreement` — for an amendment, its agreement (`root_contract_key`):
 *    its type is decided there, never on its own, so it is never `decidable`.
 *  - `decision` — set exactly when `confirmed`: an Admin's `HumanVerification` row.
 *  - `auto_confirmation` — set exactly when `auto_confirmed` (CLM-118): the
 *    `contract-type-rule` act that names nobody. Never a `decision`.
 *  - `name_evidence` — what the file-name rule found at intake (CLM-118, R-118-3);
 *    `null` = never recorded.
 *  - `ai_type_held` — CLM-118 (R-118-9): on an agreement that is `pending` or
 *    `provisional` with a usable reading, why the AI's type was not applied (the
 *    type rule's guard, computed by the same pure function the pipeline runs);
 *    `null` otherwise.
 *  - `newer_reading_disagrees` — settled (`confirmed` or `auto_confirmed`), and a reading newer than the decision
 *    disagrees with the type, or has no usable one (R-110-22). On an agreement
 *    it reads the whole family — its own reading and every amendment's — because
 *    an amendment's newer reading reopens the family exactly as the agreement's
 *    does (the decision's refusal #5 skips `no_change` for either). On an
 *    amendment it is that amendment's own reading.
 *  - `decidable` — an Admin may decide it now: an agreement, not awaiting the AI.
 *  - `seen` — set exactly when `decidable`: the attestation the decision dialog
 *    posts back unchanged, built by the API from every row with the agreement's
 *    `root_contract_key` (amendments at any depth), each with its latest reading —
 *    the same read the decision makes, so a record-opened decision matches it.
 *  - `is_current_base` — the agreement is the current base for a type, so a
 *    decision that changes its type is `409 current_base_contract` (R-110-21);
 *    the dialog pre-empts it. Always `false` on an amendment.
 *  - `legacy_key_type_segment` — "issued as a SOW ID before IDs stopped carrying
 *    the type; the type is MSA; IDs never change" (R-110-25).
 *  - `auto_approval_sample` — CLM-121 (R-121-15): a provisional agreement whose
 *    latest reading the type's auto-approval sent to an Admin as its 1-in-5
 *    safety sample. Correcting it pauses auto-approval. Always `false` on a
 *    settled type and on an amendment.
 */
export const ContractTypeStatusSchema = z
    .object({
    confirmation: ContractTypeConfirmationSchema,
    provisional_reason: ContractTypeProvisionalReasonSchema.nullable(),
    follows_agreement: ContractKeySchema.nullable(),
    decision: ContractTypeDecisionSummarySchema.nullable(),
    auto_confirmation: ContractTypeAutoConfirmationSchema.nullable(),
    name_evidence: ContractTypeNameEvidenceSchema.nullable(),
    ai_type_held: ContractTypeRuleHoldSchema.nullable(),
    newer_reading_disagrees: z.boolean(),
    decidable: z.boolean(),
    seen: ContractTypeDecisionSeenSchema.nullable(),
    is_current_base: z.boolean(),
    lineage_notes: z.array(ContractTypeLineageNoteSchema),
    legacy_key_type_segment: z.string().regex(LEGACY_KEY_TYPE_SEGMENT_PATTERN).nullable(),
    auto_approval_sample: z.boolean(),
})
    .strict()
    .refine((status) => (status.decision !== null) === (status.confirmation === 'confirmed'), {
    message: 'decision is set exactly when the type is confirmed',
    path: ['decision'],
})
    .refine((status) => (status.auto_confirmation !== null) === (status.confirmation === 'auto_confirmed'), {
    message: 'auto_confirmation is set exactly when the type is auto_confirmed',
    path: ['auto_confirmation'],
})
    .refine((status) => (status.provisional_reason !== null) === (status.confirmation === 'provisional' || status.confirmation === 'pending'), {
    message: 'provisional_reason is set exactly while the type is provisional or pending',
    path: ['provisional_reason'],
})
    .refine((status) => status.ai_type_held === null ||
    ((status.confirmation === 'provisional' || status.confirmation === 'pending') && status.follows_agreement === null), { message: 'ai_type_held is reported only on a pending or provisional agreement', path: ['ai_type_held'] })
    .refine((status) => status.follows_agreement === null || !status.decidable, {
    message: 'an amendment follows its agreement and is never decidable on its own',
    path: ['decidable'],
})
    .refine((status) => !status.newer_reading_disagrees || isSettledTypeConfirmation(status.confirmation), {
    message: 'a newer disagreeing reading is reported only on a settled type',
    path: ['newer_reading_disagrees'],
})
    .refine((status) => (status.seen !== null) === status.decidable, {
    message: 'seen is set exactly when the type is decidable',
    path: ['seen'],
})
    .refine((status) => status.seen === null ||
    (status.seen.confirmation === status.confirmation &&
        status.seen.confirmed_audit_id === ((status.decision ?? status.auto_confirmation)?.audit_id ?? null)), { message: 'seen is the type as the record shows it', path: ['seen', 'confirmation'] })
    .refine((status) => status.follows_agreement === null || !status.is_current_base, {
    message: 'an amendment is never a base',
    path: ['is_current_base'],
})
    .refine((status) => !status.auto_approval_sample || (status.confirmation === 'provisional' && status.follows_agreement === null), { message: 'a safety sample is a provisional agreement (CLM-121)', path: ['auto_approval_sample'] });
export const ContractDetailSchema = z.object({
    contract_key: ContractKeySchema,
    contract_type: ContractTypeSchema,
    /**
     * CLM-110 / DR-10 — how `contract_type` was decided. `null` = recorded before
     * CLM-110 (no backfill guesses it).
     */
    contract_type_source: ContractTypeDecidedBySchema.nullable(),
    /** CLM-110 / FR-35 — see {@link ContractTypeProposalDetailSchema}. `null` = no proposal yet. */
    contract_type_proposal: ContractTypeProposalDetailSchema.nullable(),
    /**
     * CLM-110 — whether an Admin has settled `contract_type`, why not, and what
     * the decision was. The record's one confirmation field (no second
     * `contract_type_confirmation` beside it). See {@link ContractTypeStatusSchema}.
     */
    contract_type_status: ContractTypeStatusSchema,
    /* TD-04 — two axes, never collapsed. */
    lifecycle_status: LifecycleStatusSchema,
    processing_status: ProcessingStatusSchema,
    /**
     * CLM-108 — the trust axis (B-02). `Verified` means every field was settled by
     * a person; `AutoApproved` means some were settled by policy (D-2.1-4) — the
     * record must never read "Verified" then, whatever `processing_status` says.
     */
    data_trust: DataTrustSchema,
    lineage: ContractLineageSchema,
    parties: z.array(ContractPartySchema).min(1),
    dates: ContractDatesSchema,
    key_terms: ContractKeyTermsSchema,
    /** FR-05 — non-legal summary covering terms, timeline, obligations, liability. */
    plain_summary: provenanced(z.string().max(20000).nullable()),
    /**
     * CLM-109 — every admin-defined field this contract was extracted with, in
     * key order, retired ones included. Empty for a contract extracted before
     * any admin field existed (nothing re-extracts it — Q-109-4).
     */
    additional_terms: z.array(ContractAdditionalTermSchema),
    /* §8.1 Rule 4 — these four are inherited from the parent by an amendment. */
    contract_owner_upn: UpnSchema.nullable(),
    security_classification: SecurityClassificationSchema.nullable(),
    category: z.string().max(60).nullable(),
    /** CLM-77 — not inherited by amendments (§8.1 Rule 4 covers security, not segmentation). Sorted. */
    tags: z.array(TagSchema).max(TAGS_PER_CONTRACT_MAX),
    personal_data: PersonalDataAssessmentSchema,
    documents: ContractDocumentsSchema,
    /** IR-01 — reused from the source system, never minted here. */
    sow_number: z.string().max(32).nullable(),
    intake_path: IntakePathSchema.nullable(),
    /* Compliance provenance. */
    conversion: ConversionProvenanceSchema.nullable(),
    /**
     * CLM-85 — set when conversion passed with a quality caveat; NULL for a clean pass
     * or an unconverted row. Not folded into `conversion`, which C-13 keeps `null`.
     */
    conversion_caveat: ConversionCaveatSchema.nullable(),
    /**
     * CLM-91 / D-91-3 — which cloudEQ legal entity signed: set by the deterministic
     * in-tenant scan of full_text at store-conversion (`text_scan`) or by an admin
     * (`admin`, never overwritten by a scan). NULL = Unassigned; the note says why.
     */
    cloudeq_entity_key: CloudeqEntityKeySchema.nullable(),
    cloudeq_entity_source: CloudeqEntitySourceSchema.nullable(),
    cloudeq_entity_scan_note: z.string().max(200).nullable(),
    ai_processing: AiProcessingProvenanceSchema,
    /** §4C — set at intake, inherited from the party, overridable per contract. */
    processing_restricted: z.boolean(),
    /**
     * CLM-118 (R-118-14, B13) — "possibly an amendment filed on its own": the
     * amendment word in the file or folder name (`name_word`) and the agreement the
     * AI's reading says it amends or replaces. `flagged` is the live flag (an
     * agreement with no `amends` or `supersedes` edge, not `Superseded`, not
     * dismissed, with a word or a reference). `null` when the contract has no
     * signal at all.
     */
    amendment_signal: z
        .object({
        name_word: AmendmentSignalNameSchema.nullable(),
        amends_reference: z.string().min(1).max(120).nullable(),
        supersedes_reference: z.string().min(1).max(120).nullable(),
        dismissed_utc: IsoTimestampUtcSchema.nullable(),
        dismissed_by_upn: UpnSchema.nullable(),
        flagged: z.boolean(),
    })
        .strict()
        .refine((signal) => (signal.dismissed_utc === null) === (signal.dismissed_by_upn === null), {
        message: 'a dismissal names its time and its Admin together',
        path: ['dismissed_by_upn'],
    })
        .refine((signal) => !signal.flagged || signal.dismissed_utc === null, {
        message: 'a dismissed signal is never flagged',
        path: ['flagged'],
    })
        .nullable(),
    /**
     * §8.1 Rule 3 — a `Superseded` contract is read-only. Returned so the front end
     * can disable its own controls; the API and a database trigger both reject the
     * write regardless.
     */
    is_read_only: z.boolean(),
    created_utc: IsoTimestampUtcSchema,
    modified_utc: IsoTimestampUtcSchema,
});
//# sourceMappingURL=contract.js.map